import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Reports.scss';
import {
    getReportSummaries,
    getUserReportDetails,
    markReportViewed,
    blockUser,
    unBlockUser,
} from '../../api/apiServiceFetch';
import { DataGrid, Column, Paging, Pager, Sorting } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { Button } from 'devextreme-react/button';
import userPng from './user.png'
import { getFileBaseUrl, TELEGRAM_BASE_URL } from '../../config/environment';

const PAGE_SIZE = 20;

// Объявлено снаружи не ради красоты. Таблица сравнивает настройки по ссылке, и
// литерал в разметке был бы новым объектом на каждую перерисовку: таблица
// считала бы настройки изменившимися и перезагружала страницу. А страница при
// загрузке меняет состояние (счётчики шапки, признак загрузки) — то есть
// вызывает следующую перерисовку. Получался бесконечный круг запросов.
const REMOTE_OPERATIONS = { paging: true, sorting: false, filtering: false };
const ALLOWED_PAGE_SIZES = [10, 20, 50];

// devextreme-react отдаёт виджет то свойством, то функцией — зависит от версии.
const getGridInstance = (ref) => {
    const grid = ref?.current;
    if (!grid) return null;
    return typeof grid.instance === 'function' ? grid.instance() : grid.instance;
};

const Reports = () => {
    const [selectedUserReports, setSelectedUserReports] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
    // Счётчики шапки приходят с сервера по всей выборке, а не по странице.
    const [totals, setTotals] = useState({ totalReportsCount: 0, newReportsCount: 0 });
    const gridRef = useRef(null);
    const detailsRequestRef = useRef(0);
    const filePath = getFileBaseUrl();

    // Поиск с задержкой: меняем не сам ввод, а то, что уходит на сервер —
    // иначе таблица перезагружалась бы на каждое нажатие клавиши.
    useEffect(() => {
        const handle = setTimeout(() => setAppliedSearchTerm(searchTerm), 400);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    const refreshGrid = useCallback(() => {
        getGridInstance(gridRef)?.refresh();
    }, []);

    // Страницу тянет сервер: тела жалоб сюда не приезжают вообще, только сводка
    // по каждому пользователю. Смена строки поиска пересоздаёт store, и таблица
    // сама возвращается на первую страницу.
    const reportsStore = useMemo(
        () =>
            new CustomStore({
                key: 'userId',
                load: async (loadOptions) => {
                    const take = loadOptions.take || PAGE_SIZE;
                    const skip = loadOptions.skip || 0;
                    const pageNumber = Math.floor(skip / take) + 1;

                    setLoading(true);
                    try {
                        const response = await getReportSummaries({
                            pageNumber,
                            pageSize: take,
                            searchTerm: appliedSearchTerm,
                        });

                        const rows = Array.isArray(response?.data) ? response.data : [];
                        // Новый объект на каждую загрузку — это гарантированная
                        // перерисовка, даже когда цифры те же. Держим прежний,
                        // если ничего не изменилось: перерисовка страницы во
                        // время её же загрузки — самый короткий путь к циклу.
                        const nextTotals = {
                            totalReportsCount: response?.totalReportsCount ?? 0,
                            newReportsCount: response?.newReportsCount ?? 0,
                        };
                        setTotals((prev) =>
                            prev.totalReportsCount === nextTotals.totalReportsCount &&
                            prev.newReportsCount === nextTotals.newReportsCount
                                ? prev
                                : nextTotals,
                        );
                        setError((prev) => (prev === null ? prev : null));

                        return {
                            data: rows,
                            totalCount: response?.totalCount ?? rows.length,
                        };
                    } catch (err) {
                        console.error('Failed to load reports:', err);
                        setError('Не удалось загрузить жалобы. Попробуйте позже.');
                        throw new Error('Не удалось загрузить жалобы. Попробуйте позже.');
                    } finally {
                        setLoading(false);
                    }
                },
            }),
        [appliedSearchTerm],
    );

    // Тексты жалоб грузятся только по клику на строку — это и есть та часть
    // ответа, ради которой раньше тормозил весь экран.
    const loadUserReports = useCallback(async (userId) => {
        const requestId = ++detailsRequestRef.current;
        setDetailsLoading(true);
        try {
            const details = await getUserReportDetails(userId);
            if (requestId !== detailsRequestRef.current) return;
            setSelectedUserReports(
                (Array.isArray(details) ? details : []).map((report) => ({
                    ...report,
                    isViewed: report.isViewed || false,
                    userId,
                })),
            );
        } catch (err) {
            if (requestId !== detailsRequestRef.current) return;
            console.error(`Failed to load reports for user ${userId}:`, err);
            setError('Не удалось загрузить тексты жалоб.');
            setSelectedUserReports([]);
        } finally {
            if (requestId === detailsRequestRef.current) setDetailsLoading(false);
        }
    }, []);

    // Всё, что уходит в таблицу пропсом, ниже обёрнуто в useCallback намеренно.
    // Таблица сравнивает настройки по ссылке: обычная функция, объявленная в теле
    // компонента, каждый раз новая, таблица считает колонки изменившимися и
    // перезагружает страницу с сервера. Раньше это было безобидно — данные
    // лежали в памяти целиком; теперь каждая такая «перенастройка» — сетевой
    // запрос, а запрос меняет состояние и вызывает следующую перерисовку.
    // Зависимости подобраны так, чтобы при загрузке страницы не менялась ни одна.
    const handleRowClick = useCallback((e) => {
        setSelectedUserId(e.data.userId);
        setSelectedUserReports([]);
        loadUserReports(e.data.userId);
    }, [loadUserReports]);

    const handleMarkViewed = async (reportId, userId) => {
        setProcessing(prev => ({ ...prev, [`view-${reportId}`]: true }));
        try {
            await markReportViewed(reportId);
            refreshGrid();
            if (selectedUserId === userId) {
                setSelectedUserReports(prev =>
                    (prev || []).map(report =>
                        report.reportId === reportId ? { ...report, isViewed: true } : report
                    )
                );
            }
        } catch (err) {
            setError('Не удалось отметить жалобу как просмотренную.');
            console.error('Failed to mark report as viewed:', err);
        } finally {
            setProcessing(prev => ({ ...prev, [`view-${reportId}`]: false }));
        }
    };

    const handleBlockUser = useCallback(async (userId) => {
        setProcessing(prev => ({ ...prev, [`block-${userId}`]: true }));
        try {
            const data = { userId, reason: "ЖАЛОБЫ" };
            const success = await blockUser(data);
            if (success) {
                refreshGrid();
                if (selectedUserId === userId) {
                    setSelectedUserReports(null);
                    setSelectedUserId(null);
                }
            }
        } catch (err) {
            setError('Не удалось заблокировать пользователя.');
            console.error(`Failed to block user ${userId}:`, err);
        } finally {
            setProcessing(prev => ({ ...prev, [`block-${userId}`]: false }));
        }
    }, [selectedUserId, refreshGrid]);

    const handleUnBlockUser = useCallback(async (userId) => {
        setProcessing(prev => ({ ...prev, [`unblock-${userId}`]: true }));
        try {
            const success = await unBlockUser(userId);
            if (success) {
                refreshGrid();
                if (selectedUserId === userId) {
                    setSelectedUserReports(null);
                    setSelectedUserId(null);
                }
            }
        } catch (err) {
            setError('Не удалось разблокировать пользователя.');
            console.error(`Failed to unblock user ${userId}:`, err);
        } finally {
            setProcessing(prev => ({ ...prev, [`unblock-${userId}`]: false }));
        }
    }, [selectedUserId, refreshGrid]);

    const renderUserCell = useCallback((data) => {
        const { userUsername, userFullName, userMainPhoto } = data.data;
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>

                {userUsername && userUsername.trim()?.length > 0 ? (
                    <>
                        <a
                            href={`${TELEGRAM_BASE_URL}/${userUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <img
                                src={userMainPhoto?.length > 0 ? `${filePath}/Photo/${data.data.userId}/${userMainPhoto}` : userPng}
                                alt={`Фото ${userUsername}`}
                                className="user-photo"
                            />
                        </a>
                        <span style={{ color: '#1d9bf0', fontWeight: 'bold', marginLeft: "10px" }}>
                            {userFullName}
                            <br />
                            {userUsername}
                        </span>
                    </>
                ) : (
                    <>
                        <img
                            src={userMainPhoto?.length > 0 ? `${filePath}/Photo/${data.data.userId}/${userMainPhoto}` : userPng}
                            alt={`Фото ${userUsername}`}
                            className="user-photo"
                        />
                        <span style={{ marginLeft: '10px' }}>{userFullName}</span>
                    </>
                )}
            </div>
        );
    }, [filePath]);

    const renderActionCell = useCallback((data) => {
        const { userId, isBlocked, newReportsCount } = data.data;
        const allViewed = (newReportsCount ?? 0) === 0;
        return (
            <div className="action-buttons">
                {isBlocked ? (
                    <Button
                        className="unblock-button"
                        text={processing[`unblock-${userId}`] ? 'Разблокировка...' : 'Разблокировать'}
                        onClick={() => handleUnBlockUser(userId)}
                        disabled={processing[`unblock-${userId}`]}
                    />
                ) : (
                    <Button
                        className="block-button"
                        text={processing[`block-${userId}`] ? 'Блокировка...' : 'Блокировать'}
                        onClick={() => handleBlockUser(userId)}
                        disabled={!allViewed || processing[`block-${userId}`]}
                    />
                )}
            </div>
        );
    }, [processing, handleBlockUser, handleUnBlockUser]);

    const renderDetailUserCell = (data) => {
        const { fromUserUsername, fromUserFullName, fromUserId } = data.data;
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {fromUserUsername && fromUserUsername.trim()?.length > 0 ? (
                    <>
                        <a
                            href={`${TELEGRAM_BASE_URL}/${fromUserUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                        >
                            <img
                                src={data.data.fromUserMainPhoto?.length > 0 ? `${filePath}/Photo/${fromUserId}/${data.data.fromUserMainPhoto}` : userPng}
                                alt={`Фото ${fromUserUsername}`}
                                className="user-photo"
                            />
                        </a>

                        <span style={{ color: '#1d9bf0', marginLeft: '10px' }}>
                            {fromUserUsername}
                            <br />
                            {fromUserFullName}
                            <br />
                            ID: {fromUserId}
                        </span>

                    </>

                ) : (
                    <>
                        <img
                            src={data.data.fromUserMainPhoto?.length > 0 ? `${filePath}/Photo/${fromUserId}/${data.data.fromUserMainPhoto}` : userPng}
                            alt={`Фото ${fromUserFullName}`}
                            className="user-photo"
                        />
                        <span style={{ marginLeft: '10px' }}>
                            {fromUserFullName}
                            <br />
                            ID: {fromUserId}
                        </span>
                    </>
                )}
            </div>
        );
    };

    const renderDetailActionCell = (data) => {
        const { reportId, userId, isViewed } = data.data;
        return (
            <Button
                className="view-button"
                text={isViewed ? 'Просмотрено' : processing[`view-${reportId}`] ? 'Обработка...' : 'Отметить просмотренным'}
                onClick={() => handleMarkViewed(reportId, userId)}
                disabled={isViewed || processing[`view-${reportId}`]}
            />
        );
    };

    const onRowPrepared = useCallback((e) => {
        if (e.rowType === 'data') {
            if (e.data.isBlocked) {
                e.rowElement.classList.add('blocked');
            }
            if (e.data.userId === selectedUserId) {
                e.rowElement.classList.add('selected');
            }
        }
    }, [selectedUserId]);

    return (
        <div className="reports-container">
            <h2>Жалобы</h2>
            <div className="report-counts">
                <span>Всего: {totals.totalReportsCount}</span>
                <span>Новые: {totals.newReportsCount}</span>
            </div>
            <div className="reports-search">
                <input
                    type="text"
                    className="reports-search-input"
                    placeholder="Поиск: ID, legacy ID, почта, имя или фамилия"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div className="reports-hint">
                Сначала непросмотренные, затем по дате последней жалобы. Тексты жалоб
                открываются по клику на строку.
            </div>
            {error && <div className="reports-error">{error}</div>}
            {loading && <div className="reports-loading">Загрузка...</div>}
            <DataGrid
                ref={gridRef}
                dataSource={reportsStore}
                keyExpr="userId"
                remoteOperations={REMOTE_OPERATIONS}
                showBorders={true}
                wordWrapEnabled={true}
                className="reports-table"
                hoverStateEnabled={true}
                onRowClick={handleRowClick}
                onRowPrepared={onRowPrepared}
                columnHidingEnabled={true}
                noDataText="Жалоб нет"
            >
                {/* Порядок задаёт сервер: на клиенте лежит только текущая страница,
                    и сортировать её было бы враньём. */}
                <Sorting mode="none" />
                <Paging defaultPageSize={PAGE_SIZE} />
                <Pager
                    showPageSizeSelector={true}
                    allowedPageSizes={ALLOWED_PAGE_SIZES}
                    showInfo={true}
                    showNavigationButtons={true}
                />
                <Column dataField="userId" caption="Код пользователя" width={120} />
                <Column dataField="legacyUserId" caption="TG id" width={120} />
                <Column dataField="userName" caption="Жалоба на пользователя" cellRender={renderUserCell} />
                <Column dataField="newReportsCount" caption="Новые жалобы" width={130} />
                <Column dataField="totalReportsCount" caption="Всего жалоб" width={130} />
                <Column
                    dataField="lastReportDate"
                    caption="Последняя жалоба"
                    width={150}
                    dataType="date"
                    format="dd.MM.yyyy HH:mm"
                />
                <Column dataField="isBlocked" caption="Действие" width={200} cellRender={renderActionCell} />
            </DataGrid>
            {selectedUserReports && (
                <div className="details-container">
                    <h3>Детали жалоб</h3>
                    {detailsLoading && <div className="reports-loading">Загрузка жалоб...</div>}
                    <DataGrid
                        dataSource={selectedUserReports}
                        keyExpr="reportId"
                        showBorders={true}
                        wordWrapEnabled={true}
                        className="details-table"
                        hoverStateEnabled={true}
                        columnHidingEnabled={true}
                        noDataText={detailsLoading ? 'Загрузка...' : 'Жалоб нет'}
                    >
                        <Sorting mode="multiple" />
                        <Paging enabled={true} pageSize={5} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[10]}
                            showInfo={true}
                        />
                        <Column dataField="reportId" caption="Код" width={80} alignment="center" />
                        <Column caption="Кем подана жалоба" cellRender={renderDetailUserCell} calculateSortValue={(data) => data.fromUserUsername} />
                        <Column dataField="report" caption="Содержание жалобы" width={300} />
                        <Column
                            dataField="cDate"
                            caption="Дата"
                            width={150}
                            dataType="date"
                            format="dd.MM.yyyy HH:mm"
                        />
                        <Column caption="Действие" width={250} cellRender={renderDetailActionCell} />
                    </DataGrid>
                </div>
            )}
        </div>
    );
};

export default Reports;
